<script>
  /*____________ Config  ____________*/
  export let config;
  const tourConfig = config;
  const { callToAction, audioSettings, menu, theme, form } = tourConfig;
  import { tourFunctions } from "../tour-functions";
  const { convertColor } = tourFunctions;
  // callout color fallback to primary color for backwards compatibility
  const calloutColor = theme.calloutColor || theme.primaryColor;
  const colors = {
    themeColor: convertColor(theme.primaryColor),
    controlsTextColor: convertColor(theme.controlsTextColor || "#FFFFFF"),
    loaderColor: convertColor(theme.loaderColor),
    loaderBg: convertColor(theme.loaderBgColor),
    hotspotHoverColor: convertColor(theme.hotspotColor),
    calloutColor: convertColor(calloutColor),
    hotspotColor: convertColor(theme.hotspotColor),
    hotspotTextColor: convertColor(theme.hotspotTextColor),
    popupTextColor: convertColor(theme.popupTextColor || "#FFFFFF"),
    popupBgColor: convertColor(theme.popupBgColor || theme.primaryColor),
    sceneButtonBgColor: convertColor(
      theme.sceneButtonBgColor || theme.primaryColor
    ),
    sceneButtonTextColor: convertColor(theme.sceneButtonTextColor || "#FFFFFF"),
  };
  tourFunctions.setupColors(colors);
  const fonts = {
    fontHeading: tourConfig.fontHeading || "Oswald",
    fontBody: tourConfig.fontBody || "Raleway",
  };
  tourFunctions.setupFonts(fonts);

  /*____________ Imports ____________*/
  import { fly } from "svelte/transition";
  import { getContext, onMount } from "svelte";
  const { open } = getContext("simple-modal");

  /* ____________ Mobile Detection ____________ */
  import UA from "ua-parser-js";
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";

  // Live Feedback
  let liveFeedback;

  /*____________ Components ____________*/
  import Krpano from "./Krpano.svelte";
  import Mapbox from "./Mapbox.svelte";
  import SwitchViews from "./SwitchViews.svelte";
  import MapButton from "./MapButton.svelte";
  import InfoSidebar from "./InfoSidebar.svelte";
  import Logo from "./Logo.svelte";
  import HamburgerMenu from "./HamburgerMenu.svelte";
  import NextScene from "./NextScene.svelte";
  import PrevScene from "./PrevScene.svelte";
  import WatermarkLogo from "./WatermarkLogo.svelte";
  import ThumbsGrid from "./ThumbsGrid.svelte";
  import SceneTitle from "./SceneTitle.svelte";
  import Video from "./Video.svelte";
  import Audio from "./Audio.svelte";
  import PopupHotspot from "./PopupHotspot.svelte";
  import SceneButton from "./SceneButton.svelte";
  import PopupGallery from "./PopupGallery.svelte";
  import CallToAction from "./CallToAction.svelte";
  import FormPopup from "./FormPopup.svelte";
  import Popup from "./Popup.svelte";
  import Ada from "./Ada.svelte";
  import Instructions from "./Instructions.svelte";

  /*____________ Variables ____________*/
  let visibleUi = false;
  let video;
  const tourLogo = isMobile ? tourConfig.logoMobile : tourConfig.logo;
  const mapLogo = tourConfig.mapLogo || tourLogo;
  const logoLoading = tourConfig.logoLoading || tourLogo;
  const tourTitle = tourFunctions.parseContent(tourConfig.name);
  const sceneButtonSettings = tourConfig.sceneButton
    ? tourConfig.sceneButton
    : {};
  // audio
  let sceneAudioPlaying;
  // Map variables
  let expandedMap = false;
  let expandedTour = true;
  let zoom;
  let sceneButtonLabel = sceneButtonSettings?.label ?? "Overview";
  const autoSize = sceneButtonSettings?.autoSize !== false;
  // scenebutton width
  let sceneButtonWidth = tourLogo.width * 0.8;
  if (!autoSize) {
    sceneButtonWidth =
      !isMobile && sceneButtonSettings.width
        ? sceneButtonSettings.width
        : isMobile && sceneButtonSettings.mobileWidth
          ? sceneButtonSettings.mobileWidth
          : sceneButtonWidth;
  }
  // scenebutton x position
  let sceneButtonX = tourLogo.width / 2 - sceneButtonWidth / 2 + 20;
  if (!autoSize) {
    sceneButtonX =
      !isMobile && sceneButtonSettings.x
        ? sceneButtonSettings.x
        : isMobile && sceneButtonSettings.mobileX
          ? sceneButtonSettings.mobileX
          : sceneButtonX;
  }
  // scenebutton y position
  let sceneButtonY = tourLogo.height + 25;
  if (!autoSize) {
    sceneButtonY =
      !isMobile && sceneButtonSettings.y
        ? sceneButtonSettings.y
        : isMobile && sceneButtonSettings.mobileY
          ? sceneButtonSettings.mobileY
          : sceneButtonY;
  }

  document.addEventListener("focusin", (e) => {
    console.log("focusin", e.target);
  });

  document.addEventListener("keydown", function (event) {
    switch (event.key) {
      // Hide/Show UI
      case "H":
      case "h":
        if (event.shiftKey) {
          visibleUi = !visibleUi;
          if (visibleUi) {
            tourFunctions.showHotspots();
            liveFeedback.textContent = "Hotkey Hide Interface pressed";
          } else {
            tourFunctions.hideHotspots();
            liveFeedback.textContent = "Hotkey Show Interface pressed";
          }
        }

        break;
      // Show Instructions
      case "i":
        toggleInstructions();
        if (instructionsVisible) {
          liveFeedback.textContent = "Hotkey Show Instructions pressed";
        } else {
          liveFeedback.textContent = "Hotkey Hide Instructions pressed";
        }
        break;
      // Refocus the krpano element
      case "Escape":
        tourFunctions.focus();
        liveFeedback.textContent = "Hotkey Escape pressed";
        break;
    }
  });

  /*____________ Functions ____________*/
  function showUi() {
    visibleUi = true;
  }

  import { fade } from "svelte/transition";
  let formPopupOptions = {
    closeButton: true,
    closeOnEsc: true,
    closeOnOuterClick: false,
    transitionBg: fade,
    transitionBgProps: {
      duration: 250,
    },
    transitionWindow: fly,
    transitionWindowProps: {
      y: window.innerHeight,
      duration: 1000,
    },
    styleBg: { backgroundColor: "rgba(0, 0, 0, 1)" },
    styleWindow: {
      width: "80vw", // set width to 80vw
      height: "80vh", // set height to 80vh
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "2em",
    },
    styleContent: { maxHeight: "100%", width: "100%" },
    styleCloseButton: { top: "-5px", right: "-5px" },
  };

  // setup map
  if (typeof tourConfig.map === "object") {
    setMapZoom();
  }

  function getHotspotContent(hotspot) {
    console.log(hotspot);
    if (typeof hotspot === "string") {
      hotspot = tourFunctions.currentScene.getHotspot(hotspot);
    }

    let gallery = null;
    if (hotspot.popup_gallery) {
      gallery = tourFunctions.currentScene.getGallery(hotspot.popup_gallery);
    }

    const {
      name,
      popup_title,
      popup_text,
      popup_image,
      popup_image_alt,
      popup_video_embed,
    } = hotspot;
    const title = popup_title ? tourFunctions.parseContent(popup_title) : "";
    const content = popup_text ? tourFunctions.parseContent(popup_text) : "";

    return {
      name,
      title,
      image: popup_image,
      imageAlt: popup_image_alt,
      content,
      videoUrl: popup_video_embed,
      gallery: gallery,
      handleVideoLoad: function () {
        video = popup_video_embed;
        sceneAudioPlaying = false;
        // loadVideo(hotspot.popup_video_embed);
      },
    };
  }

  function openHotspotInfo(popupContent) {
    // if hotspotName was passed instead of {object}
    //      create {popupContent} object from hotspot attributes
    if (typeof popupContent === "string") {
      let hotspotName = popupContent;
      popupContent = getHotspotContent(hotspotName);
    }

    new PopupHotspot({
      target: document.getElementById("popup-container"),
      props: { ...popupContent },
    });
  }

  function openGalleryInfo(hotspot) {
    if (typeof hotspot === "string") {
      hotspot = tourFunctions.getHotspot(hotspot);
    }

    let gallery = tourFunctions.currentScene.getGallery(hotspot.popup_gallery);

    let popupContent = {
      name: hotspot.name,
      title: tourFunctions.parseContent(hotspot.hotspot_title),
      gallery: gallery,
    };

    new PopupGallery({
      target: document.getElementById("popup-container"),
      props: { ...popupContent },
    });
  }

  /*____________ Krpano Events ____________*/
  addEventListener("krpano:littleplanetend", (e) => {
    console.log(e);
    showUi();
  });

  // DISABLE IF InfoSidebar is being used
  if (!tourConfig.infoSidebar) {
    addEventListener("krpano:infoclick", (e) => {
      let hotspotName = e.detail.hotspot;
      openHotspotInfo(hotspotName);
      console.log("krpano:infoclick", e);
    });
  }

  addEventListener("krpano:galleryclick", (e) => {
    console.log(e);
    let hs = tourFunctions.currentScene.getHotspot(e.detail.hotspot);
    openGalleryInfo(hs);
  });

  let menuActive;
  let adaVisible = false;
  let instructionsVisible = false;
  let hasExecutedQuerystring = false;
  // Handle Popup Ada & Instructions
  $: if (visibleUi && !hasExecutedQuerystring) {
    // ?ada query string
    const urlParams = new URLSearchParams(window.location.search);
    const adaSlug = tourConfig.accessibilitySlug || "ada";
    const adaParam = urlParams.get(adaSlug);
    const hasAdaParam = adaParam === "true" || adaParam === "";
    if (hasAdaParam) adaVisible = true;
    hasExecutedQuerystring = true; // Set the flag to true after execution
  }

  function handleHamburgerAda() {
    adaVisible = true;
    menuActive = false;
  }

  function handleHamburgerInstructions() {
    instructionsVisible = true;
    menuActive = false;
  }

  function toggleInstructions() {
    instructionsVisible = !instructionsVisible;
  }

  function closeAda() {
    adaVisible = false;
    tourFunctions.focus();
  }

  function closeInstructions() {
    instructionsVisible = false;
    tourFunctions.focus();
  }

  // Open Videos
  function loadVideo(v) {
    video = v;
    sceneAudioPlaying = false;
  }

  function closeVideo() {
    sceneAudioPlaying = true;
    tourFunctions.focus();
  }

  function handleVideoLoadEvent(e) {
    loadVideo(e.detail.video);
  }

  addEventListener("krpano:videoclick", (e) => {
    console.log(e);
    handleVideoLoadEvent(e);
  });

  // Scene logos
  // if(tourConfig.sceneLogos){
  //     addEventListener('krpano:newpano', (e)=>{
  //         let sceneLogo = tourFunctions.currentScene.getLogo();
  //         tourLogo.url = sceneLogo ? sceneLogo : tourConfig.logo.url;
  //     });
  // }

  function switchView() {
    expandedTour = !expandedTour;
    expandedMap = !expandedMap;
    expandedTour ? tourFunctions.showHotspots() : tourFunctions.hideHotspots();
    setMap();
  }

  function setMap() {
    window.CC = window.CC || {};
    window.CC.switchView = switchView;

    let zoomExpandedMap = 15.5;
    let zoomMiniMap = 14;
    zoom = expandedMap ? zoomExpandedMap : zoomMiniMap;
    return zoom;
  }
  if (tourConfig.map) {
    setMap();
  }
</script>

<!-- /*____________ Markup ____________*/ -->

{#if !tourConfig.map}
  <Krpano
    config={tourConfig}
    {colors}
    expanded={expandedTour}
    xml="tour.xml"
    logoLoading={logoLoading.url}
    logoLoadingWidth={logoLoading.width}
  />
{/if}

{#if tourConfig.map}
  <div class={expandedTour ? "large-view" : "small-view"}>
    <div class="hide-large" on:click={switchView}>
      <div class="switch-hover">Show Map</div>
      <SwitchViews />
    </div>
    <Krpano
      config={tourConfig}
      {colors}
      expanded={expandedTour}
      xml="tour.xml"
      logoLoading={logoLoading.url}
      logoLoadingWidth={logoLoading.width}
    />
  </div>

  {#if visibleUi}
    <div
      class={expandedMap ? "large-view" : "small-view"}
      transition:fly={{ y: 300, duration: 500 }}
    >
      <div class="hide-large" on:click={switchView}>
        <div class="switch-hover">Show Tour</div>
        <SwitchViews />
      </div>
      <Mapbox expanded={expandedMap} {zoom} />
    </div>
  {/if}
{/if}

{#if form && form.enabled}
  <FormPopup {...form} />
{/if}
<div id="popup-container"></div>
<Popup visible={adaVisible} on:close={closeAda} ariaLabel="Accessibility Tour">
  <Ada tour={config} />
</Popup>
<Popup visible={instructionsVisible} on:close={closeInstructions}>
  <Instructions />
</Popup>
{#if visibleUi}
  {#if tourConfig.infoSidebar === true}
    <InfoSidebar on:loadvideo={handleVideoLoadEvent} logo={tourLogo} />
  {/if}
  <!-- has map -->
  {#if tourConfig.map}
    <MapButton click={switchView} {expandedMap} />
  {/if}

  <Video bind:src={video} onClose={closeVideo} />

  <!-- no sidebar -->
  {#if !tourConfig.infoSidebar}
    <Logo
      image={expandedMap ? mapLogo.url : tourLogo.url}
      width={expandedMap ? mapLogo.width : tourLogo.width}
      opacity="0.85"
    />
    {#if sceneButtonSettings?.enabled !== false}
      <SceneButton
        text={sceneButtonLabel}
        x={sceneButtonX}
        y={sceneButtonY}
        width={sceneButtonWidth}
        opacity={0.8}
      />
    {/if}
  {/if}

  {#if callToAction}
    <CallToAction {callToAction} />
  {/if}

  {#if menu}
    <HamburgerMenu
      menus={menu}
      tour={config}
      bind:active={menuActive}
      on:ada={handleHamburgerAda}
      on:instructions={handleHamburgerInstructions}
    />
  {/if}

  {#if tourConfig.hasWatermark === true}
    <WatermarkLogo />
  {/if}

  {#if expandedTour}
    {#if !isMobile}
      <PrevScene />
      <NextScene />
    {/if}
    <SceneTitle />
    <ThumbsGrid
      title={tourTitle}
      showPrevNext={false}
      thumbsButtonOpen={"Open Locations"}
      thumbsButtonClose={"Close Locations"}
      hasCategories={tourConfig.features?.includes("categories")}
      categories={tourConfig.categories}
      scenes={tourConfig.scenes.filter((scene) => !scene.hidden)}
    />
  {/if}

  {#if audioSettings?.enabled && typeof audioSettings === "object"}
    <Audio
      loop={audioSettings.looping}
      resumable={audioSettings.resumable}
      playing={sceneAudioPlaying}
    />
  {:else if audioSettings === true || tourConfig?.features?.includes("audio")}
    <Audio playing={sceneAudioPlaying} />
  {/if}
{/if}

<div
  aria-live="assertive"
  id="liveFeedback"
  bind:this={liveFeedback}
  style="position: absolute; left: -9999px; top: -9999px;"
></div>

<!-- /*____________ CSS ____________*/ -->
<style>
  :global(.clicktext) {
    font-weight: 900;
    font-size: 14px;
    text-decoration: underline;
  }

  .switch-hover {
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    position: absolute;
    left: 0;
    top: 0;
    z-index: 9;
    color: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1.5rem;
    font-family: var(--font-body), sans-serif;
    display: none;
    cursor: pointer;

    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .small-view:hover .switch-hover {
    display: flex;
  }

  .small-view {
    display: block;
    width: 300px;
    height: 200px;

    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 800;

    margin: 20px;

    border: 3px solid white;
    border-radius: 10px;
    box-shadow: -3px 3px 10px #000;
    background: white;
  }

  .large-view {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    z-index: 700;
  }

  .large-view .hide-large {
    display: none;
  }

  :global(.large-view .mapboxgl-map) {
    height: 100vh;
    width: 100vw;
  }

  :global(.small-view .mapboxgl-map) {
    height: 200px;
    width: 300px;
  }

  @media only screen and (max-width: 600px) {
    .small-view {
      display: none;
    }
  }
</style>
